import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Loader2 } from 'lucide-react'
import type { Season } from './types'

interface SeasonFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  editingSeason: Season | null
  // Form state
  name: string
  onNameChange: (value: string) => void
  startDate: string
  onStartDateChange: (value: string) => void
  endDate: string
  onEndDateChange: (value: string) => void
  signupsOpen: boolean
  onSignupsOpenChange: (value: boolean) => void
  registrationMode: 'anyone' | 'group_members_only'
  onRegistrationModeChange: (value: 'anyone' | 'group_members_only') => void
  terms: string
  onTermsChange: (value: string) => void
  hasFee: boolean
  onHasFeeChange: (value: boolean) => void
  feeAmount: string
  onFeeAmountChange: (value: string) => void
  feeCurrency: string
  onFeeCurrencyChange: (value: string) => void
  feeDescription: string
  onFeeDescriptionChange: (value: string) => void
  hasGameLimit: boolean
  onHasGameLimitChange: (value: boolean) => void
  maxGamesPerOpponent: number
  onMaxGamesPerOpponentChange: (value: number) => void
  showBankDetails: boolean
  onShowBankDetailsChange: (value: boolean) => void
  hasBankDetails: boolean // Whether group has bank details configured
  paymentReference: string
  onPaymentReferenceChange: (value: string) => void
  groupDefaultReference?: string // Group's default payment reference
  // Actions
  onSave: () => Promise<void>
  onCancel: () => void
  saving: boolean
  error: string | null
}

export function SeasonFormDialog({
  open,
  onOpenChange,
  editingSeason,
  name,
  onNameChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  signupsOpen,
  onSignupsOpenChange,
  registrationMode,
  onRegistrationModeChange,
  terms,
  onTermsChange,
  hasFee,
  onHasFeeChange,
  feeAmount,
  onFeeAmountChange,
  feeCurrency,
  onFeeCurrencyChange,
  feeDescription,
  onFeeDescriptionChange,
  hasGameLimit,
  onHasGameLimitChange,
  maxGamesPerOpponent,
  onMaxGamesPerOpponentChange,
  showBankDetails,
  onShowBankDetailsChange,
  hasBankDetails,
  paymentReference,
  onPaymentReferenceChange,
  groupDefaultReference,
  onSave,
  onCancel,
  saving,
  error,
}: SeasonFormDialogProps) {
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      onCancel()
    }
    onOpenChange(newOpen)
  }

  const isFutureSeason = !startDate || startDate >= new Date().toISOString().split('T')[0]

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingSeason ? 'Edit Season' : 'New Season'}</DialogTitle>
          <DialogDescription>
            {editingSeason ? 'Update the season details' : 'Create a new season for this league'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label>Season Name</Label>
            <Input placeholder="e.g., Spring 2025" value={name} onChange={e => onNameChange(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input type="date" value={startDate} onChange={e => onStartDateChange(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input type="date" value={endDate} onChange={e => onEndDateChange(e.target.value)} />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label className="text-sm font-medium">Open for Signups</Label>
              <p className="text-xs text-muted-foreground">Allow players to register for this season</p>
            </div>
            <Switch
              checked={signupsOpen}
              onCheckedChange={onSignupsOpenChange}
            />
          </div>

          {/* Registration mode - only show when signups open */}
          {signupsOpen && (
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <Label className="text-sm font-medium">Who Can Register</Label>
                <p className="text-xs text-muted-foreground">
                  {registrationMode === 'anyone' 
                    ? 'Anyone can register (public seasons)' 
                    : 'Only group members can register'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs ${registrationMode === 'group_members_only' ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                  Members
                </span>
                <Switch
                  checked={registrationMode === 'anyone'}
                  onCheckedChange={(checked) => onRegistrationModeChange(checked ? 'anyone' : 'group_members_only')}
                />
                <span className={`text-xs ${registrationMode === 'anyone' ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                  Anyone
                </span>
              </div>
            </div>
          )}
          
          {/* Terms & Conditions and Joining Fee - only show for future seasons */}
          {isFutureSeason && (
            <>
              <div className="space-y-2">
                <Label>Terms & Conditions</Label>
                <Textarea
                  placeholder="Optional terms and conditions for joining this season..."
                  value={terms}
                  onChange={e => onTermsChange(e.target.value)}
                  rows={3}
                  className="text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Players will see these when signing up
                </p>
              </div>
              
              <div className="space-y-3 rounded-lg border p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Joining Fee</Label>
                    <p className="text-xs text-muted-foreground">Charge a fee to join this season</p>
                  </div>
                  <Switch
                    checked={hasFee}
                    onCheckedChange={onHasFeeChange}
                  />
                </div>
                
                {hasFee && (
                  <div className="space-y-2 pt-2 border-t">
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2 space-y-1">
                        <Label className="text-xs text-muted-foreground">Amount</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="10.00"
                          value={feeAmount}
                          onChange={e => onFeeAmountChange(e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Currency</Label>
                        <select
                          value={feeCurrency}
                          onChange={e => onFeeCurrencyChange(e.target.value)}
                          className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        >
                          <option value="GBP">£ GBP</option>
                          <option value="USD">$ USD</option>
                          <option value="EUR">€ EUR</option>
                        </select>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Description (optional)</Label>
                      <Input
                        placeholder="e.g., Covers court fees and shuttlecocks"
                        value={feeDescription}
                        onChange={e => onFeeDescriptionChange(e.target.value)}
                      />
                    </div>
                    
                    {/* Show bank details toggle */}
                    <div className="flex items-center justify-between pt-2 border-t">
                      <div>
                        <Label className="text-xs font-medium">Show Bank Details</Label>
                        <p className="text-xs text-muted-foreground">
                          {hasBankDetails 
                            ? 'Display group bank details when players register'
                            : 'Configure bank details in group settings first'}
                        </p>
                      </div>
                      <Switch
                        checked={showBankDetails}
                        onCheckedChange={onShowBankDetailsChange}
                        disabled={!hasBankDetails}
                      />
                    </div>
                    
                    {/* Payment reference override */}
                    {showBankDetails && (
                      <div className="space-y-1 pt-2 border-t">
                        <Label className="text-xs text-muted-foreground">Payment Reference</Label>
                        <Input
                          placeholder={groupDefaultReference || 'e.g., SEASON-{name}'}
                          value={paymentReference}
                          onChange={e => onPaymentReferenceChange(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">
                          {groupDefaultReference 
                            ? `Leave empty to use group default: "${groupDefaultReference}"`
                            : 'Override the payment reference for this season'}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-3 rounded-lg border p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Game Limit Per Opponent</Label>
                    <p className="text-xs text-muted-foreground">Limit how many games players can have against the same opponent</p>
                  </div>
                  <Switch
                    checked={hasGameLimit}
                    onCheckedChange={onHasGameLimitChange}
                  />
                </div>
                
                {hasGameLimit && (
                  <div className="pt-2 border-t">
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={1}
                        max={50}
                        value={maxGamesPerOpponent}
                        onChange={e => onMaxGamesPerOpponentChange(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-20"
                      />
                      <span className="text-sm text-muted-foreground">games maximum per opponent</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Players can only submit {maxGamesPerOpponent} match{maxGamesPerOpponent !== 1 ? 'es' : ''} against the same opponent this season
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
          
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onCancel}>Cancel</Button>
            <Button onClick={onSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editingSeason ? 'Save Changes' : 'Create Season'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
